import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb, Users, Cog, HeadphonesIcon, CheckCircle2, Target, Rocket, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

const approachSteps = [
  {
    icon: Lightbulb,
    title: "Collaborative Discovery",
    description: "We begin every engagement by deeply understanding your business, challenges, and aspirations. Through workshops, interviews, and analysis, we uncover the opportunities that will drive the most value.",
    details: [
      "Stakeholder interviews and alignment sessions",
      "Current state assessment and gap analysis",
      "Industry benchmarking and best practices review",
      "Opportunity identification and prioritization",
      "Vision and goals definition"
    ]
  },
  {
    icon: Target,
    title: "Strategic Planning",
    description: "Our experts craft a tailored roadmap that aligns technology initiatives with your business objectives. We ensure every recommendation is actionable, measurable, and delivers clear ROI.",
    details: [
      "Solution architecture and design",
      "Technology selection and evaluation",
      "Implementation roadmap development",
      "Resource planning and timeline estimation",
      "Risk assessment and mitigation strategies"
    ]
  },
  {
    icon: Cog,
    title: "Expert Implementation",
    description: "Our skilled teams deliver solutions with precision, quality, and speed. We follow industry best practices and agile methodologies to ensure successful outcomes.",
    details: [
      "Agile development methodology",
      "Continuous integration and deployment",
      "Quality assurance and testing",
      "Change management and training",
      "Documentation and knowledge transfer"
    ]
  },
  {
    icon: HeadphonesIcon,
    title: "Ongoing Support",
    description: "We stand by you with continuous support, monitoring, and optimization. Our partnership doesn't end at go-live – we're committed to your long-term success.",
    details: [
      "24/7 production support options",
      "Performance monitoring and optimization",
      "Regular health checks and reviews",
      "Continuous improvement recommendations",
      "Knowledge base and self-service tools"
    ]
  }
];

const principles = [
  {
    icon: Users,
    title: "Client-Centric Focus",
    description: "Your success is our success. We measure our performance by the value we deliver to your business."
  },
  {
    icon: Rocket,
    title: "Innovation-Driven",
    description: "We stay at the forefront of technology trends to bring you cutting-edge solutions that provide competitive advantage."
  },
  {
    icon: Shield,
    title: "Quality Obsessed",
    description: "We never compromise on quality. Every deliverable meets the highest standards of excellence."
  },
  {
    icon: CheckCircle2,
    title: "Transparent Communication",
    description: "We believe in open, honest communication. You'll always know where things stand and what's coming next."
  }
];

export default function ConsultingApproach() {
  return (
    <div data-testid="consulting-approach-page" className="page-transition">
      {/* Hero Section */}
      <section className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400 mb-4">
              Our Methodology
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-white font-['Outfit'] mb-6">
              Our Consulting Approach
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed font-['Plus_Jakarta_Sans']">
              A proven methodology that ensures successful outcomes. We combine deep technical 
              expertise with a collaborative approach to deliver solutions that drive real business value.
            </p>
          </div>
        </div>
      </section>

      {/* Approach Steps */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {approachSteps.map((step, stepIdx) => (
              <div 
                key={step.title}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start ${
                  stepIdx % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className={stepIdx % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-6xl lg:text-7xl font-black text-slate-100 font-['Outfit']">
                      {String(stepIdx + 1).padStart(2, '0')}
                    </span>
                    <div className="w-14 h-14 bg-blue-100 flex items-center justify-center">
                      <step.icon className="w-7 h-7 text-blue-700" />
                    </div>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight mb-4">
                    {step.title}
                  </h2>
                  <p className="text-slate-600 leading-relaxed mb-6 font-['Plus_Jakarta_Sans']">
                    {step.description}
                  </p>
                </div>
                
                <div className={`bg-slate-50 p-8 border border-slate-200 ${stepIdx % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 mb-4">
                    Key Activities
                  </h3>
                  <ul className="space-y-3">
                    {step.details.map((detail) => (
                      <li key={detail} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600 font-['Plus_Jakarta_Sans']">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles Section */}
      <section className="bg-slate-50 py-20 lg:py-28 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
              Guiding Principles
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-['Outfit']">
              What Sets Us Apart
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {principles.map((principle) => (
              <div key={principle.title} className="bg-white p-8 border border-slate-200">
                <div className="w-12 h-12 bg-blue-100 flex items-center justify-center mb-6">
                  <principle.icon className="w-6 h-6 text-blue-700" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-3">
                  {principle.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed font-['Plus_Jakarta_Sans']">
                  {principle.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-700 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-blue-100 mb-8 font-['Plus_Jakarta_Sans']">
            Let's discuss how our proven approach can help transform your business.
          </p>
          <Link to="/contact">
            <Button 
              className="bg-white text-blue-700 hover:bg-slate-100 rounded-none px-8"
            >
              Contact Us
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
